import * as i0 from '@angular/core';
import { Directive, HostListener, Injectable, Component, ViewChild, Input, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { isString } from 'lodash-es';
import { interval, Subject, fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import * as i1 from '@angular/platform-browser';

function postMessage(data) {
    window.parent.postMessage('fs-iframe:' + JSON.stringify(data), '*');
}

class FsIFrameScrollToDirective {
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
    }
    ngOnInit() {
        this.renderer.addClass(this.elementRef.nativeElement, 'fs-iframe-scrollto');
    }
    onClick(event) {
        postMessage({ name: 'scrollto', value: this.elementRef.nativeElement.getBoundingClientRect().top });
    }
}
FsIFrameScrollToDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrameScrollToDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
FsIFrameScrollToDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsIFrameScrollToDirective, selector: "[fsIFrameScrollTo]", host: { listeners: { "click": "onClick($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrameScrollToDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsIFrameScrollTo]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

function parseEvent(event) {
    if (event.data && isString(event.data)) {
        const matches = event.data.match(/(fs-iframe):(.*)/);
        if (matches) {
            try {
                return JSON.parse(matches[2]);
            }
            catch (e) { }
        }
    }
    return { name: null, value: null };
}

class FsIFrameDirective {
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.offset = 0;
    }
    ngOnInit() {
        this.renderer.addClass(this.elementRef.nativeElement, 'fs-iframe');
    }
    onMessage(event) {
        const data = parseEvent(event);
        if (data.name === 'height') {
            const height = data.value;
            this.renderer.setStyle(this.elementRef.nativeElement, 'height', height + 'px');
        }
        else if (data.name === 'scrollto') {
            window.document.documentElement.scrollTop = data.value + this.offset;
        }
        else if (data.name === 'offset') {
            this.offset = data.value;
        }
    }
}
FsIFrameDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrameDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
FsIFrameDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: FsIFrameDirective, selector: "[fsIFrame]", host: { listeners: { "window:message": "onMessage($event)" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrameDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsIFrame]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { onMessage: [{
                type: HostListener,
                args: ['window:message', ['$event']]
            }] } });

class FsIFrame {
    observeBody() {
        return interval(100)
            .subscribe(() => {
            const height = window.document.body.offsetHeight;
            if (this.height !== height) {
                this.updateHeight(height);
                this.height = height;
            }
        });
    }
    updateCurrentHeight() {
        this.updateHeight(window.document.body.offsetHeight);
    }
    updateHeight(value) {
        this.update('height', value);
    }
    update(name, value) {
        postMessage({ name: name, value: value });
    }
}
FsIFrame.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrame, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FsIFrame.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrame });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrame, decorators: [{
            type: Injectable
        }] });

class FsIFrameComponent {
    constructor(_domSaniizer) {
        this._domSaniizer = _domSaniizer;
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        var _a, _b;
        if (this.src) {
            if (((_a = this.src) === null || _a === void 0 ? void 0 : _a.match(/data:image/)) || ((_b = this.src) === null || _b === void 0 ? void 0 : _b.match(/\.(jpe?g|png)/))) {
                this.html = `<img src="${this.src}">`;
                this.src = null;
            }
            else {
                this.src = this._domSaniizer.bypassSecurityTrustResourceUrl(this.src);
            }
        }
    }
    onload() {
        this.updateHeight();
    }
    get frameEl() {
        var _a;
        return (_a = this.frame) === null || _a === void 0 ? void 0 : _a.nativeElement;
    }
    ngAfterViewInit() {
        fromEvent(window, 'resize')
            .pipe(debounceTime(50), takeUntil(this._destroy$))
            .subscribe(() => {
            this.updateHeight();
        });
        if (this.html) {
            this._updateBodyFrames();
        }
    }
    _updateBodyFrames() {
        const win = this.frameEl.contentWindow;
        const doc = win.document;
        const data = `
    <style>
      body {
        font-family: Roboto;
        font-size: 15px;
        margin: 0 !important;
        overflow-y: hidden !important;
        width: auto !important;
      }

      a {
        color: #1155CC;
      }

      * {
        box-sizing: border-box !important;
      }

      ${this.styles}
    </style>
    ${this.html}`;
        doc.open();
        doc.write(data);
        doc.close();
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    updateHeight() {
        var _a, _b;
        if ((_b = (_a = this.frameEl) === null || _a === void 0 ? void 0 : _a.contentDocument) === null || _b === void 0 ? void 0 : _b.body) {
            if (this.height) {
                this.frameEl.setAttribute('height', this.height);
            }
            else {
                this.frameEl.removeAttribute('height');
                this.frameEl.setAttribute('height', this.frameEl.contentDocument.body.scrollHeight);
            }
            if (this.width) {
                this.frameEl.setAttribute('width', this.width);
            }
            else {
                this.frameEl.removeAttribute('width');
                this.frameEl.setAttribute('width', this.frameEl.contentDocument.body.scrollWidth);
            }
        }
    }
}
FsIFrameComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrameComponent, deps: [{ token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component });
FsIFrameComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: FsIFrameComponent, selector: "fs-iframe", inputs: { html: "html", src: "src", styles: "styles", width: "width", height: "height" }, viewQueries: [{ propertyName: "frame", first: true, predicate: ["frame"], descendants: true }], ngImport: i0, template: "<ng-container *ngIf=\"src\">\n  <iframe \n    class=\"iframe-src\"\n    #frame \n    [src]=\"src\" \n    (load)=\"onload()\">\n  </iframe>\n</ng-container>\n\n<ng-container *ngIf=\"html\">\n  <iframe \n    class=\"iframe-html\"\n    #frame \n    (load)=\"onload()\">\n  </iframe>\n</ng-container>", styles: ["iframe{border:0}iframe.iframe-src{width:100%}\n"], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrameComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fs-iframe',
                    templateUrl: './iframe.component.html',
                    styleUrls: ['./iframe.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: i1.DomSanitizer }]; }, propDecorators: { frame: [{
                type: ViewChild,
                args: ['frame']
            }], html: [{
                type: Input
            }], src: [{
                type: Input
            }], styles: [{
                type: Input
            }], width: [{
                type: Input
            }], height: [{
                type: Input
            }] } });

class FsIFrameModule {
    static forRoot() {
        return {
            ngModule: FsIFrameModule,
            providers: [FsIFrame]
        };
    }
}
FsIFrameModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrameModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsIFrameModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrameModule, declarations: [FsIFrameScrollToDirective,
        FsIFrameDirective,
        FsIFrameComponent], imports: [CommonModule], exports: [FsIFrameScrollToDirective,
        FsIFrameDirective,
        FsIFrameComponent] });
FsIFrameModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrameModule, imports: [[
            CommonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: FsIFrameModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                    ],
                    exports: [
                        FsIFrameScrollToDirective,
                        FsIFrameDirective,
                        FsIFrameComponent,
                    ],
                    declarations: [
                        FsIFrameScrollToDirective,
                        FsIFrameDirective,
                        FsIFrameComponent,
                    ],
                }]
        }] });

// Modules

/**
 * Generated bundle index. Do not edit.
 */

export { FsIFrame, FsIFrameComponent, FsIFrameDirective, FsIFrameModule, FsIFrameScrollToDirective };
